import { useEffect, useRef, useState } from "react";
import { PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Upload, Cpu } from "lucide-react";
import { toast } from "sonner";

/**
 * PoseExtractor — Phase 2 client-side pose pipeline.
 *
 * Flow:
 *   1. Coach picks a local MP4/MOV
 *   2. We POST it to /motioniq/upload-video to get a served URL
 *   3. We create a capture (POST /motioniq/captures)
 *   4. Load MediaPipe Tasks Vision (BlazePose Full)
 *   5. Seek the hidden <video> frame-by-frame, run poseLandmarker.detectForVideo,
 *      map landmarks → our BONES-compatible keys, derive joint_angles_3d,
 *      batch every 30 frames and POST /captures/{id}/frames
 *   6. On completion, hand the capture back to the parent Studio
 *
 * Runs entirely in the browser — no server-side GPU required.
 */

// BlazePose 33-landmark → ArmIQ skeleton key map. Values are the
// landmark indices from https://developers.google.com/mediapipe/solutions/vision/pose_landmarker
const LANDMARK_MAP = {
  head: 0,
  shoulder_l: 11, shoulder_r: 12,
  elbow_l: 13,    elbow_r: 14,
  wrist_l: 15,    wrist_r: 16,
  hip_l: 23,      hip_r: 24,
  knee_l: 25,     knee_r: 26,
  ankle_l: 27,    ankle_r: 28,
};

// Derived (midpoint) joints we synthesize from raw landmarks so the
// Studio bones render cleanly.
function derive(pts) {
  const mid = (a, b) => a && b ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] : null;
  const shoulderMid = mid(pts.shoulder_l, pts.shoulder_r);
  const hipMid = mid(pts.hip_l, pts.hip_r);
  const headMid = pts.head && shoulderMid ? [(pts.head[0] + shoulderMid[0]) / 2, (pts.head[1] + shoulderMid[1]) / 2] : null;
  if (shoulderMid) pts.thorax = shoulderMid;
  if (hipMid) pts.pelvis = hipMid;
  if (headMid) pts.cervical = headMid;
  return pts;
}

// Compute a few coach-friendly joint angles from world-space landmarks.
// All returned in degrees.
function angleBetween(a, b, c) {
  // Angle at joint b, formed by segments b→a and b→c.
  if (!a || !b || !c) return null;
  const v1 = [a[0] - b[0], a[1] - b[1], (a[2] || 0) - (b[2] || 0)];
  const v2 = [c[0] - b[0], c[1] - b[1], (c[2] || 0) - (b[2] || 0)];
  const dot = v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2];
  const mag1 = Math.hypot(...v1);
  const mag2 = Math.hypot(...v2);
  if (mag1 === 0 || mag2 === 0) return null;
  const cos = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

function computeAngles(world) {
  // world is same shape as pts but populated from world_landmarks (meters).
  const shoulder_r = world.shoulder_r, elbow_r = world.elbow_r, wrist_r = world.wrist_r;
  const shoulder_l = world.shoulder_l, hip_r = world.hip_r, hip_l = world.hip_l;
  const ankle_l = world.ankle_l, ankle_r = world.ankle_r;
  const thorax = world.thorax || (shoulder_l && shoulder_r ? [(shoulder_l[0] + shoulder_r[0]) / 2, (shoulder_l[1] + shoulder_r[1]) / 2, ((shoulder_l[2] || 0) + (shoulder_r[2] || 0)) / 2] : null);
  const pelvis = world.pelvis || (hip_l && hip_r ? [(hip_l[0] + hip_r[0]) / 2, (hip_l[1] + hip_r[1]) / 2, ((hip_l[2] || 0) + (hip_r[2] || 0)) / 2] : null);

  const elbow_flexion = angleBetween(shoulder_r, elbow_r, wrist_r);
  // Hip-shoulder separation approx: angle between shoulder line and hip line projected onto horizontal plane.
  let hip_shoulder_separation = null;
  if (shoulder_l && shoulder_r && hip_l && hip_r) {
    const sh = [shoulder_r[0] - shoulder_l[0], shoulder_r[2] - shoulder_l[2]];
    const hp = [hip_r[0] - hip_l[0], hip_r[2] - hip_l[2]];
    const dot = sh[0] * hp[0] + sh[1] * hp[1];
    const m1 = Math.hypot(...sh), m2 = Math.hypot(...hp);
    if (m1 && m2) {
      const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
      hip_shoulder_separation = (Math.acos(cos) * 180) / Math.PI;
    }
  }
  // Trunk tilt: angle of thorax-pelvis vector from vertical (y-axis).
  let trunk_tilt = null;
  if (thorax && pelvis) {
    const v = [thorax[0] - pelvis[0], thorax[1] - pelvis[1], thorax[2] - pelvis[2]];
    const mag = Math.hypot(...v);
    if (mag) trunk_tilt = (Math.acos(Math.abs(-v[1]) / mag) * 180) / Math.PI;
  }

  // ── Pitcher stride length ──────────────────────────────────────────
  // Horizontal (ground-plane) distance between the two ankles, meters.
  // We normalize by an estimated athlete height derived from the world
  // skeleton: thorax→pelvis + pelvis→ankle_mid. That gives us a stable
  // "stride as % of height" figure per frame — the studio reports the
  // MAX across frames as the peak stride at foot-strike.
  let stride_length_m = null;
  let stride_length_pct_height = null;
  if (ankle_l && ankle_r) {
    const dx = ankle_r[0] - ankle_l[0];
    const dz = (ankle_r[2] || 0) - (ankle_l[2] || 0);
    stride_length_m = Math.hypot(dx, dz);
    if (thorax && pelvis) {
      const spine = Math.hypot(thorax[0] - pelvis[0], thorax[1] - pelvis[1], (thorax[2] || 0) - (pelvis[2] || 0));
      const ankle_mid_y = (ankle_l[1] + ankle_r[1]) / 2;
      const legs = Math.abs(pelvis[1] - ankle_mid_y);
      const est_height_m = spine + legs;
      if (est_height_m > 0.2) {
        stride_length_pct_height = +((stride_length_m / est_height_m) * 100).toFixed(1);
      }
    }
  }

  return {
    elbow_flexion,
    hip_shoulder_separation,
    trunk_tilt,
    // Shoulder external rotation surrogate: angle of upper arm relative to torso.
    shoulder_er: angleBetween(hip_r, shoulder_r, elbow_r),
    stride_length_m,
    stride_length_pct_height,
  };
}

const MODEL_ASSET = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task";
const WASM_ROOT = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

export default function PoseExtractor({ athleteUserId, mode = "pitching", onCaptureReady }) {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");     // idle | uploading | extracting | done | error
  const [progress, setProgress] = useState(0);      // 0..1
  const [message, setMessage] = useState("");
  const [captureId, setCaptureId] = useState(null);

  const start = async () => {
    if (!file) { toast.error("Pick a video first"); return; }
    setStatus("uploading");
    setMessage("Uploading video…");
    setProgress(0);
    try {
      // 1) Upload the video
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/motioniq/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const videoUrl = up.data.video_url;

      // 2) Create a capture
      const cap = await api.post("/motioniq/captures", {
        athlete_user_id: athleteUserId,
        mode,
        label: `Auto · ${file.name}`,
        video_url: videoUrl,
        video_fps: 30,
        duration_ms: 0,
      });
      const capId = cap.data.capture_id;
      setCaptureId(capId);

      // 3) Load the video into a hidden element to seek frames
      const el = videoRef.current;
      el.src = URL.createObjectURL(file);
      await new Promise((res, rej) => {
        el.onloadedmetadata = res;
        el.onerror = () => rej(new Error("Could not load video"));
      });
      const durationMs = Math.round(el.duration * 1000);
      // Persist duration on the capture so the Studio's scrubber has
      // the right upper bound even before any frames are posted.
      await api.patch(`/motioniq/captures/${capId}`, {
        duration_ms: durationMs,
        video_fps: 30,
      }).catch(() => { /* endpoint optional */ });

      // 4) Load MediaPipe PoseLandmarker
      setStatus("extracting");
      setMessage("Loading BlazePose model…");
      const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);
      const landmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_ASSET, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
      });

      // 5) Iterate frames at ~30fps
      const fps = 30;
      const totalFrames = Math.max(1, Math.floor((el.duration || 5) * fps));
      const batch = [];
      const BATCH_SIZE = 30;
      const w = el.videoWidth || 1280;
      const h = el.videoHeight || 720;
      let framesPosted = 0;

      const seekTo = (t) => new Promise((res) => {
        const onSeeked = () => { el.removeEventListener("seeked", onSeeked); res(); };
        el.addEventListener("seeked", onSeeked);
        el.currentTime = t;
      });

      for (let i = 0; i < totalFrames; i++) {
        const t = i / fps;
        await seekTo(t);
        const tsMs = Math.round(t * 1000);
        const result = landmarker.detectForVideo(el, performance.now());
        const lm = result.landmarks?.[0];
        const world = result.worldLandmarks?.[0];
        if (lm && world) {
          const pts = {};
          const worldPts = {};
          Object.entries(LANDMARK_MAP).forEach(([key, idx]) => {
            const p = lm[idx];
            if (p) pts[key] = [Math.round(p.x * w), Math.round(p.y * h)];
            const wp = world[idx];
            if (wp) worldPts[key] = [wp.x, wp.y, wp.z];
          });
          derive(pts);
          derive(worldPts);
          const angles = computeAngles(worldPts);
          batch.push({
            frame_id: i,
            timestamp_ms: tsMs,
            joint_pixels_2d: pts,
            joint_angles_3d: angles,
            live_telemetry: {},
          });
        }
        if (batch.length >= BATCH_SIZE) {
          await api.post(`/motioniq/captures/${capId}/frames`, { frames: batch.splice(0) });
          framesPosted = i + 1;
        }
        setProgress((i + 1) / totalFrames);
        setMessage(`Extracting pose · frame ${i + 1}/${totalFrames}`);
        // Yield to keep the UI responsive
        if (i % 5 === 0) await new Promise((r) => setTimeout(r, 0));
      }
      if (batch.length > 0) {
        await api.post(`/motioniq/captures/${capId}/frames`, { frames: batch });
      }
      landmarker.close();
      setStatus("done");
      setMessage(`Extracted ${totalFrames} frames`);
      toast.success("Pose extraction complete");
      onCaptureReady?.(capId);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setMessage(err?.message || "Extraction failed");
      toast.error(err?.response?.data?.detail || err?.message || "Extraction failed");
    }
  };

  useEffect(() => () => {
    if (videoRef.current?.src?.startsWith("blob:")) {
      URL.revokeObjectURL(videoRef.current.src);
    }
  }, []);

  const busy = status === "uploading" || status === "extracting";

  return (
    <div className="ink-card rounded-xl p-4 space-y-3" data-testid="pose-extractor">
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-fuchsia-400" /> Auto pose extract · BlazePose
      </div>
      <p className="text-[11px] text-white/50 leading-snug">
        Upload an MP4/MOV — MediaPipe extracts 33-point pose per frame in your browser and streams it straight into a new capture.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept="video/mp4,video/mov,video/webm,video/m4v,video/*"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid="pose-file-input"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
          className="text-white/80 text-[11px] font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.09]"
          data-testid="pose-pick-file">
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Pick video
        </Button>
        {file && (
          <span className="text-[11px] text-white/60 truncate flex-1" data-testid="pose-file-name">
            {file.name}
          </span>
        )}
      </div>
      <Button
        onClick={start}
        disabled={busy || !file}
        className="w-full text-white text-xs font-bold"
        style={{ backgroundColor: "var(--brand-secondary)" }}
        data-testid="pose-extract-start"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Cpu className="h-4 w-4 mr-2" />}
        {status === "idle" && "Extract pose → new capture"}
        {status === "uploading" && "Uploading…"}
        {status === "extracting" && "Extracting…"}
        {status === "done" && "Done · run another"}
        {status === "error" && "Retry"}
      </Button>
      {(busy || status === "done") && (
        <div className="space-y-1" data-testid="pose-progress">
          <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full transition-all"
                 style={{ width: `${Math.round(progress * 100)}%`, background: "var(--brand-secondary)" }} />
          </div>
          <div className="text-[10px] text-white/50 font-mono-num">{message}</div>
        </div>
      )}
      {captureId && status === "done" && (
        <div className="text-[10px] text-white/50 font-mono-num" data-testid="pose-capture-id">
          capture_id · {captureId}
        </div>
      )}
      {/* Hidden playback surface for MediaPipe to read frames from */}
      <video ref={videoRef} className="hidden" muted playsInline crossOrigin="anonymous" />
    </div>
  );
}
